import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Breadcrumbs,
  Container,
  LinearProgress,
  IconButton,
  Grid,
  Paper,
  Stack,
  Typography,
  Snackbar,
  TextField,
  Skeleton,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SpeakerImage from '../../media/Lamp Poses-02-1.png';
import RefreshIcon from '@mui/icons-material/Refresh';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import UserAvatar from '../../components/UserAvatar';
import DeleteButton from '../../components/DeleteButton';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { AuthContext } from '../../AuthContext';

function Index() {
  const jApi = useApi();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [account, setAccount] = useState(null);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const { user, getUser } = useContext(AuthContext);
  const [accounts, setAccounts] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [loadingButton, setLoadingButton] = useState(null);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const user = getUser();
      let selected_account = sessionStorage.getItem('account');
      const urlAccount = 'cma/account?code=' + selected_account;
      const responseAccount = await jApi.request('GET', urlAccount);
      setAccount(responseAccount.data[0]);

      const response = await jApi.request('GET', 'cma/account');
      const options = response.data
        .map((item) => `${item.name} (${item.code})`)
        .sort((a, b) => a.localeCompare(b));

      setAccounts(options);

      let selectedItem = options.find((option) =>
        option.includes('(' + selected_account + ')')
      );
      if (selectedItem) {
        setSelectedAccount(selectedItem);
      } else {
        setSelectedAccount(options[0]);
      }
    } catch (error) {
      setErrorMessage(error.toString());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function extractCodeFromLabel(label) {
    const match = label.match(/\((.*?)\)/);
    return match ? match[1] : null;
  }

  const handleOnChange = (event, newValue) => {
    if (!newValue) {
      return;
    }
    setSelectedAccount(newValue);
    sessionStorage.setItem('account', extractCodeFromLabel(newValue));
    fetchData();
  };

  const onDeviceReport = async (functionpath) => {
    try {
      setErrorMessage('');
      setLoadingButton(functionpath);

      const body = {
        functionpath: functionpath,
        splat: {
          emailaddresses: user.username,
          account: sessionStorage.getItem('account'),
        },
      };

      const responseAccount = await jApi.request('POST', 'ves/job', body);

      setSnackbarMessage('Your report has been queued for processing');
      setSnackbarOpen(true);
    } catch (ex) {
      setErrorMessage(ex.toString());
    } finally {
      setLoadingButton(null);
    }
  };

  const onSubmit = async () => {
    try {
      setErrorMessage('');
      setLoadingButton('Project Report');

      const body = {
        functionpath: 'cma/management/projects/Send-ProjectRpt.ps1',
        splat: {
          EmailAddresses: user.username,
        },
      };
      const responseAccount = await jApi.request('POST', 'ves/job', body);

      setSnackbarMessage('Your report has been queued for processing');
      setSnackbarOpen(true);
    } catch (ex) {
      setErrorMessage(ex.toString());
    } finally {
      setLoadingButton(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Report Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    p: 3,
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Report Center
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Generate and download comprehensive reports.
                    </Typography>
                  </Box>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          <Grid size={12}>
            <Grow in timeout={800}>
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, #1e3a8a 0%, #1e1e1e 50%, #6b21a8 100%)`
                    : `linear-gradient(135deg, #dbeafe 0%, #ffffff 50%, #f3e8ff 100%)`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                }}
              >
                <Stack direction="column" spacing={3}>
                  <Box>
                    {accounts ? (
                      <Autocomplete
                        options={accounts || []}
                        value={selectedAccount || null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Account"
                            variant="outlined"
                            placeholder="Select Account"
                          />
                        )}
                        onChange={handleOnChange}
                        sx={{
                          maxWidth: 400,
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.paper, 0.5),
                          },
                        }}
                      />
                    ) : (
                      <Skeleton variant="rectangular" height={56} sx={{ maxWidth: 400, borderRadius: 1 }} />
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-CustomerSummary')}
                        disabled={loadingButton === 'cma/report/Send-CustomerSummary'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-CustomerSummary' ? 'Loading...' : 'Customer Summary'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-DeviceSummary')}
                        disabled={loadingButton === 'cma/report/Send-DeviceSummary'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-DeviceSummary' ? 'Loading...' : 'Device Summary'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-DeviceDetails')}
                        disabled={loadingButton === 'cma/report/Send-DeviceDetails'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-DeviceDetails' ? 'Loading...' : 'Device Details'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-ContactSummary')}
                        disabled={loadingButton === 'cma/report/Send-ContactSummary'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-ContactSummary' ? 'Loading...' : 'Contact Summary'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-TrainingSummary')}
                        disabled={loadingButton === 'cma/report/Send-TrainingSummary'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-TrainingSummary' ? 'Loading...' : 'Training Summary'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-TrainingMonthly')}
                        disabled={loadingButton === 'cma/report/Send-TrainingMonthly'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-TrainingMonthly' ? 'Loading...' : 'Training Monthly'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onDeviceReport('cma/report/Send-M365LicenseSummary')}
                        disabled={loadingButton === 'cma/report/Send-M365LicenseSummary'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'cma/report/Send-M365LicenseSummary' ? 'Loading...' : 'M365 License Summary'}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={onSubmit}
                        disabled={loadingButton === 'Project Report'}
                        sx={{ py: 1.5 }}
                      >
                        {loadingButton === 'Project Report' ? 'Loading...' : 'Project Report'}
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>
            </Grow>
          </Grid>

          {errorMessage && (
            <Grid size={12}>
              <Fade in>
                <Paper
                  sx={{
                    p: 2,
                    background: alpha(theme.palette.error.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" color="error.main">
                    {errorMessage}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          )}
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </Container>
    </Box>
  );
}

export default Index;